import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Plus, RefreshCw, Pencil, Copy, Eye, Users, Mail, Lock, Ban, Send, Download } from "lucide-react";
import { API, PROGRAM_DISPLAY_BADGE } from "./shared";
import { shortDate, usd, FORMAT_INFO, opensLabel } from "@/lib/training";
import OfferingEditor, { toEditor } from "./OfferingEditor";

const GROUPS = [
  ["active", "Active & open", (p) => ["open", "limited", "full"].includes(p.display_status) && p.format === "four_week"],
  ["trials", "One-day trials", (p) => ["open", "limited", "full", "coming_soon", "closed"].includes(p.display_status) && p.format === "trial"],
  ["upcoming", "Upcoming (scheduled / coming soon)", (p) => ["coming_soon", "closed"].includes(p.display_status) && p.format === "four_week"],
  ["draft", "Drafts", (p) => p.display_status === "draft"],
  ["completed", "Completed", (p) => p.display_status === "completed"],
  ["canceled", "Canceled & archived", (p) => ["canceled", "archived"].includes(p.display_status)],
];

export async function downloadCsv(token, kind, programId = "") {
  const res = await axios.get(`${API}/admin/export/${kind}`, { headers: { "X-Admin-Token": token }, params: { program_id: programId }, responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(res.data);
  a.download = `replab-${kind}${programId ? `-${programId.slice(0, 8)}` : ""}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function Act({ testid, onClick, icon: Icon, children, tone = "" }) {
  return <button data-testid={testid} onClick={onClick} className={`inline-flex items-center gap-1.5 text-xs font-bold border border-white/15 rounded-full px-3 py-1.5 transition-colors ${tone || "text-white/70 hover:text-white"}`}><Icon size={12} /> {children}</button>;
}

function Row({ p, cats, a }) {
  const badge = PROGRAM_DISPLAY_BADGE[p.display_status] || PROGRAM_DISPLAY_BADGE.draft;
  const dates = p.session_dates || [];
  const fields = [
    ["Category", cats[p.program_type]?.name || p.program_type], ["Format", FORMAT_INFO[p.format]?.short], ["Age group", p.age_group || "—"],
    ["Schedule", [p.day, p.time].filter(Boolean).join(" at ") || "—"], ["Start", dates[0] ? shortDate(dates[0]) : "—"], ["Price", usd(p.price)],
    ["Enrollment", `${p.registered} of ${p.capacity}`], ["Drop-ins", p.dropin_enabled ? `On · ${usd(p.dropin_price || 40)}` : "Off"], ["Status", p.status === "scheduled" || p.display_status === "coming_soon" ? (p.registration_open_at ? `Opens ${opensLabel(p.registration_open_at)}` : "Coming soon") : p.display_status.replace("_", " ")],
  ];
  return (
    <div data-testid={`offering-row-${p.id}`} className="rounded-2xl bg-white/5 border border-white/10 p-4 md:p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-base font-bold text-white">{p.title}</p>
        <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase ${badge}`}>{p.display_status.replace("_", " ")}</span>
      </div>
      <dl className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2">
        {fields.map(([k, v]) => <div key={k}><dt className="text-[10px] font-bold uppercase tracking-wider text-white/35">{k}</dt><dd className="text-sm font-semibold text-white/80 capitalize-first">{v}</dd></div>)}
      </dl>
      {p.agreement_missing?.length > 0 && <p className="mt-2 text-xs font-bold text-red-300">Missing before publishing: {p.agreement_missing.join(", ")}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        <Act testid={`offering-edit-${p.id}`} icon={Pencil} onClick={() => a.edit(p)}>Edit</Act>
        <Act testid={`offering-duplicate-${p.id}`} icon={Copy} onClick={() => a.duplicate(p)}>Duplicate</Act>
        <Act testid={`offering-preview-${p.id}`} icon={Eye} onClick={() => window.open(`/training/${p.program_type}?age=${encodeURIComponent(p.age_group)}&format=${p.format}&offering=${p.id}`, "_blank")}>Preview</Act>
        {["draft", "closed", "coming_soon"].includes(p.status) && <Act testid={`offering-publish-${p.id}`} icon={Send} onClick={() => a.edit(p)} tone="text-emerald-300">Publish…</Act>}
        {["open", "scheduled", "full"].includes(p.status) && <Act testid={`offering-close-${p.id}`} icon={Lock} onClick={() => a.status(p, "closed")}>Close Registration</Act>}
        <Act testid={`offering-regs-${p.id}`} icon={Users} onClick={() => a.regs(p)}>Registrations</Act>
        <Act testid={`offering-contact-${p.id}`} icon={Mail} onClick={() => a.contact(p)}>Contact Participants</Act>
        <Act testid={`offering-roster-${p.id}`} icon={Download} onClick={() => a.roster(p)}>Roster CSV</Act>
        {!["canceled", "archived"].includes(p.status) && <Act testid={`offering-cancel-${p.id}`} icon={Ban} onClick={() => a.cancel(p)} tone="text-red-300 hover:text-red-200">Cancel / Archive</Act>}
      </div>
    </div>
  );
}

export default function TrainingTab({ ctx }) {
  const { programs, refreshPrograms, loading, token, setView, setEnrollFilters, setCommsPreset, setError } = ctx;
  const [cats, setCats] = useState([]);
  const [catFilter, setCatFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const h = { headers: { "X-Admin-Token": token } };
  useEffect(() => { axios.get(`${API}/admin/categories`, h).then((r) => setCats(r.data.categories)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const catMap = useMemo(() => Object.fromEntries(cats.map((c) => [c.key, c])), [cats]);
  const list = programs.filter((p) => !catFilter || p.program_type === catFilter);

  const blank = (key = cats[0]?.key || "hitting") => {
    const c = catMap[key] || {};
    return toEditor({ program_type: key, title: c.name || "", description: c.short_description || "", format: "four_week", age_group: (c.age_divisions || [])[0] || "", session_list: [], time: "", end_time: "",
      location: c.default_location, price: "", capacity: 8, status: "draft", visible: true, waitlist_enabled: true, dropin_enabled: true, dropin_price: 40, full_enabled: true, dropin_cutoff_hours: 24, payment_terms: "paid_in_full", renewal_type: "one_time", session_minutes: c.default_session_minutes || 60, coach: "" }, c);
  };
  const a = {
    edit: (p) => setEditing(toEditor(p, catMap[p.program_type])),
    duplicate: (p) => {
      const { id, registered, remaining, display_status, session_list, session_dates, registration_open_at, registration_close_at, next_program_id, created_at, updated_at, ...rest } = p; // eslint-disable-line no-unused-vars
      setEditing(toEditor({ ...rest, title: p.title.replace(/\s*\((Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]* \d{4}\)$/, ""), session_list: [], status: "draft", duplicate_of: p.title }, catMap[p.program_type]));
    },
    status: async (p, status) => {
      try { await axios.post(`${API}/admin/programs/${p.id}/status`, { status }, h); await refreshPrograms(); } catch (e) { setError(e?.response?.data?.detail || "Could not update status."); }
    },
    cancel: async (p) => {
      const choice = window.prompt(`Type CANCEL to cancel "${p.title}" (hidden from the website, registrations kept) or ARCHIVE to archive it.`);
      if (choice && ["CANCEL", "ARCHIVE"].includes(choice.trim().toUpperCase())) await a.status(p, choice.trim().toUpperCase() === "CANCEL" ? "canceled" : "archived");
    },
    regs: (p) => { setEnrollFilters((f) => ({ ...f, program_id: p.id })); setView("enrollments"); },
    contact: (p) => { setCommsPreset({ filter: { type: "offering", program_id: p.id } }); setView("comms"); },
    roster: (p) => downloadCsv(token, "roster", p.id),
  };
  const onSaved = async (data) => {
    setEditing(null);
    await refreshPrograms();
    if (data.flagged_registrations > 0) window.alert(`${data.flagged_registrations} registration(s) accepted the previous details. Their signed agreements were NOT changed — they're flagged in Registrations.`);
    if (data.schedule_change?.affected > 0 && window.confirm(`Schedule updated. Send the prepared schedule-update email to ${data.schedule_change.affected} affected family(ies) now? You can review it first.`)) {
      setCommsPreset({ filter: { type: "offering", program_id: data.id }, template: "schedule_update", schedule_change_id: data.schedule_change.id });
      setView("comms");
    }
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white">Training Management</h1>
        <div className="flex gap-2">
          <button data-testid="create-offering-btn" onClick={() => setEditing(blank(catFilter || undefined))} className="inline-flex items-center gap-2 text-sm font-bold text-white bg-[#2563EB] hover:bg-[#1d4fd7] rounded-full px-5 py-2.5"><Plus size={16} /> Create Training Offering</button>
          <button data-testid="training-refresh-btn" onClick={refreshPrograms} disabled={loading} className="inline-flex items-center gap-2 text-sm font-bold text-white/70 border border-white/15 rounded-full px-4 py-2.5"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /></button>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        {cats.map((c) => {
          const n = programs.filter((p) => p.program_type === c.key && ["open", "limited", "full", "coming_soon"].includes(p.display_status)).length;
          return (
            <button key={c.key} data-testid={`training-cat-${c.key}`} onClick={() => setCatFilter(catFilter === c.key ? "" : c.key)} className={`rounded-2xl border p-4 text-left ${catFilter === c.key ? "border-white bg-white/10" : "border-white/10 bg-white/5"}`}>
              <p className="text-sm font-extrabold text-white">{c.name}</p><p className="mt-1 text-xs font-semibold text-white/50">{n} live / upcoming</p>
            </button>
          );
        })}
      </div>
      {GROUPS.map(([k, label, fn]) => {
        const items = list.filter(fn);
        if (!items.length) return null;
        return (
          <div key={k} data-testid={`training-group-${k}`} className="mt-10">
            <h2 className="text-sm font-extrabold uppercase tracking-wider text-white/50">{label} <span className="text-white/30">({items.length})</span></h2>
            <div className="mt-3 space-y-3">{items.map((p) => <Row key={p.id} p={p} cats={catMap} a={a} />)}</div>
          </div>
        );
      })}
      {editing && <OfferingEditor initial={editing} categories={cats} programs={programs} token={token} onClose={() => setEditing(null)} onSaved={onSaved} />}
    </>
  );
}
